import React from 'react';
import { Dropdown, Space, Button, MenuProps, Divider, theme } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { ChargeBeePlanId } from '@prerender/billing-shared';

import { CustomTooltip } from '~components/Tooltip';

export type DropdownOption<T> = {
  upsellTooltip?: string;
  upsellPlanId?: ChargeBeePlanId;
  label: string;
  value: T;
  key: string;
};

type DropdownWithUpsellProps<Value> = React.PropsWithChildren<{
  options: DropdownOption<Value>[];
  footerElement?: React.ReactNode;
  selectedKey: string;
  styles?: {
    menu?: React.CSSProperties;
  };
  onOptionSelected: (item: DropdownOption<Value>) => void;
  onUpsellClicked: (item: DropdownOption<Value>) => void;
}>;

function DropdownWithUpsell<Value>({
  footerElement,
  selectedKey,
  onOptionSelected,
  onUpsellClicked,
  options,
  styles,
  children,
}: DropdownWithUpsellProps<Value>) {
  const { token } = theme.useToken();

  const contentStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderRadius: token.borderRadiusLG,
    boxShadow: token.boxShadowSecondary,
  };

  const dropdownItems: MenuProps['items'] = options.map((option) => ({
    label: option.label,
    value: option.value,
    key: option.key,
    extra: option.upsellTooltip ? (
      <CustomTooltip message={option.upsellTooltip} placement="bottom">
        <Button
          type="default"
          variant="filled"
          color="blue"
          size="small"
          icon={<LockOutlined />}
          onClick={() => onUpsellClicked(option)}
        >
          Upgrade
        </Button>
      </CustomTooltip>
    ) : undefined,
  }));

  const onClick: MenuProps['onClick'] = (clickedItem) => {
    const option = options.find((item) => item.key === clickedItem.key);
    if (option && !option.upsellTooltip) {
      onOptionSelected(option);
    }
  };
  return (
    <Dropdown
      menu={{
        items: dropdownItems,
        selectedKeys: selectedKey ? [selectedKey] : undefined,
        onClick,
      }}
      trigger={['click']}
      popupRender={
        footerElement
          ? (menu) => (
              <div style={contentStyle}>
                {React.cloneElement(
                  menu as React.ReactElement<{
                    style: React.CSSProperties;
                  }>,
                  { style: { boxShadow: 'none', ...styles?.menu } }
                )}
                <Divider style={{ margin: 0 }} />
                <Space style={{ padding: token.paddingXS }}>{footerElement}</Space>
              </div>
            )
          : undefined
      }
    >
      {children}
    </Dropdown>
  );
}

export default DropdownWithUpsell;
